import { useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import NotesField from "../forms/NotesField";
import { useStore } from "../../js/state/store";
import { updateBuyer } from "../../js/api/buyersApi";
import { useAutosave } from "../../js/hooks/useAutosave";
import { useGuestMode } from "../../js/hooks/useGuestMode";
import { BUYER_FRAME_STEPS } from "../../js/utils/formatters";
import { toTitleCase } from "../../js/utils/titleCase";

function normalizeGeneral(saved) {
  if (!saved || typeof saved === "string") {
    return {
      dealBreakers: "",
      mustHaves: "",
      householdNeeds: "",
      priorities: "",
      specialNotes: typeof saved === "string" ? saved : "",
      attachments: [],
    };
  }
  return {
    dealBreakers: saved.dealBreakers || "",
    mustHaves: saved.mustHaves || "",
    householdNeeds: saved.householdNeeds || "",
    priorities: saved.priorities || "",
    specialNotes: saved.specialNotes || "",
    attachments: saved.attachments || [],
  };
}

export default function BuyerNotesPanel() {
  const { pathname } = useLocation();
  const { state, dispatch } = useStore();
  const { canPersist } = useGuestMode();
  const buyer = state.buyer.current;
  const current = BUYER_FRAME_STEPS.find((step) => step.path === pathname) || BUYER_FRAME_STEPS[0];
  const [pageNotes, setPageNotes] = useState(() => ({ ...(buyer.preferences.pageNotes || {}) }));
  const [general, setGeneral] = useState(() => normalizeGeneral(buyer.preferences.notes));

  const persist = useCallback(
    (next) => {
      if (!canPersist) return;
      const preferences = { ...buyer.preferences, pageNotes: next.pageNotes, notes: next.general };
      dispatch({ type: "UPDATE_PREFERENCES", payload: { pageNotes: next.pageNotes, notes: next.general } });
      if (buyer.id) updateBuyer(buyer.id, { ...buyer, preferences });
    },
    [buyer, canPersist, dispatch]
  );

  useAutosave(canPersist ? { pageNotes, general } : null, persist);

  function updatePage(key, value) {
    setPageNotes((currentNotes) => ({ ...currentNotes, [key]: value }));
  }

  function updateGeneral(event) {
    const { name, value } = event.target;
    setGeneral((currentNotes) => ({ ...currentNotes, [name]: value }));
  }

  return (
    <div className="sidebar-panel">
      <h3 className="sidebar-panel__title">Notes · {toTitleCase(current.label)}</h3>
      <p className="muted sidebar-panel__lead sidebar-guide">
        These notes follow the page on the right. Other pages keep their own notes.
      </p>
      <NotesField
        className="sidebar-page-note"
        label={`${toTitleCase(current.label)} notes for my agent`}
        name={current.key}
        value={pageNotes[current.key] || ""}
        onChange={(event) => updatePage(current.key, event.target.value)}
        placeholder="What should your agent remember about this part of the search?"
        rows={6}
      />
      <ol className="sidebar-note-index">
        {BUYER_FRAME_STEPS.map((step) => (
          <li key={step.key} className={step.key === current.key ? "is-current" : ""}>
            <span>{toTitleCase(step.label)}</span>
            <small>{pageNotes[step.key] ? "Noted" : "Open"}</small>
          </li>
        ))}
      </ol>
      <details className="sidebar-panel__extra">
        <summary>General notes</summary>
        <NotesField label="Deal-breakers" name="dealBreakers" value={general.dealBreakers} onChange={updateGeneral} rows={3} />
        <NotesField label="Must-haves" name="mustHaves" value={general.mustHaves} onChange={updateGeneral} rows={3} />
        <NotesField label="Household needs" name="householdNeeds" value={general.householdNeeds} onChange={updateGeneral} rows={3} />
        <NotesField label="Priorities" name="priorities" value={general.priorities} onChange={updateGeneral} rows={3} />
        <NotesField label="Anything else" name="specialNotes" value={general.specialNotes} onChange={updateGeneral} rows={4} />
      </details>
    </div>
  );
}
